<template>
  <section
    class="brand-slider container--large"
    :class="{'brand-slider--loading ' : isLoading, 'brand-slider--single' : brands.length === 1, 'brand-slider--video' : brandInfo.video }"
    :aria-roledescription="allyRoleCarousel"
  >
    <template v-if="brandInfo.video">
      <div class="image-wrapper image brand-slider__background brand-slider__background--video">
        <video autoplay muted loop playsinline class="brand-slider__video  video-lazy-bg-load" :key="brandInfo.video + randomKey()">
          <source :data-src="brandInfo.video">
          <track label="English" kind="captions" srclang="en" :data-src="brandInfo.video">
        </video>
      </div>
    </template>

    <template v-if="brandInfo.background">
      <responsive-image
        :srcset="brandInfo.background"
        class="image image--brandSlider brand-slider__background"
        :key="brandInfo.background"
      >

        <!-- Accessibility -->
        <img src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" :alt="brandInfo.title" :aria-hidden="true">
      </responsive-image>

    </template>
    <template v-else>
      <slot name="brand-bg" />
    </template>
    <div class="brand-slider__content">
      <div class="container--medium">
        <div
          class="brand-slider__copy"
          :aria-roledescription="allyRoleSlide"
          aria-live="polite"
          aria-atomic="true"
        >
          <div class="brand-slider__suptitle">
            <template v-if="brandInfo.focus">{{ brandInfo.focus }}</template>
            <template v-else>
              <slot name="brand-focus" />
            </template>
          </div>
          <div class="brand-slider__title">
            <template v-if="brandInfo.title">{{ brandInfo.title }}</template>
            <template v-else>
              <slot name="brand-name" />
            </template>
          </div>
          <div class="brand-slider__baseline halesworth">
            <template v-if="brandInfo.baseline">{{ brandInfo.baseline }}</template>
            <template v-else>
              <slot name="brand-baseline" />
            </template>
          </div>
          <div class="brand-slider__text">
            <template v-if="brandInfo.text">{{ brandInfo.text }}</template>
            <template v-else>
              <slot name="brand-copy" />
            </template>
          </div>
          <a
            :href="brandInfo.link"
            :key="randomKey()"
            class="brand-slider__btn btn btn--ghost"
            v-tag:useractionevent = " {
              category: 'content',
              action: 'select::brand slider',
              label: currentBrand ,
              event_name: 'slider_button_click',
              cta_name: brandInfo.label,
              link_url: linkUrl}"
          >{{ brandInfo.label }}</a>
        </div>
        <slot name="filters" :updateFilter="updateFilter" :selected="selected" />
      </div>
    </div>
    <div class="brand-slider__slider">
      <slider
        v-if="mounted"
        id="brand-slider-filtered"
        :key="sliderKey"
        :per-page-custom="slidePerPage"
        :per-page-with-active="5"
        :navigation-enabled="!noBrandFound?true:false"
        :pagination-enabled="false"
        :loop="isSliderLoop"
        :loop-start-at="sliderStartAt"
        ref="slider"
        :aria-label="allyChooseSlide"
        role="group"
        @pageChange="updateCarousel">

        <template v-if="brands.length > 0 && !noBrandFounds">
          <slide-item
            v-for="(item, index) in brands"
            :key="item.GUID"
            class="brand-slider__slide"
            :data-GUID="item.GUID"
          >
            <button
              type="button"
              :data-index="index"
              :aria-label="item.name">
              <img
                :src="item.logo"
                alt=""
                class="brand-slider__logo"
              >
            </button>
          </slide-item>
        </template>
        <template v-else-if="noBrandFound == true"/>

        <template v-else>
          <slot name="slides"/>
        </template>

      </slider>
    </div>
  </section>
</template>

<script>
  import ResponsiveImage from '@Foundation/ResponsiveImage/code/Scripts/components/responsive-image.vue';
  import Slider from '@Foundation/Slider/code/Scripts/components/slider/slider';
  import SlideItem from '@Foundation/Slider/code/Scripts/components/slide/slideItem';
  import { fetchData } from '@Foundation/services';
  import { srcset } from '@Foundation/directives/srcset/srcset';
  import { tag } from '@Foundation/analyticsHandler/directives/tag.js';
  import { checkScreenSize } from '@Foundation/mixins/checkScreenSize';
  import { videoLazyObserver } from '../../../../../Foundation/Core/code/Scripts/utilities/intersectionViewportObserver';

  export default {
    name: 'brand-slider',

    components: {
      ResponsiveImage,
      Slider,
      SlideItem,
      'loader': () => import('@Feature/Loader/code/Script/Loader'),
    },
    directives: {
      srcset,
      tag
    },

    mixins: [checkScreenSize],
    props: {
      brandsUrl: {
        type: String,
        default: null
      },
      filterUrl: {
        type: String,
        default: null
      },
      arraySize: {
        type: String,
        default: null
      },
      allyRoleCarousel: {
        type: String,
        default: 'Carousel'
      },
      allyRoleSlide: {
        type: String,
        default: 'Slide'
      },
      allyChooseSlide: {
        type: String,
        default: 'Choose a slide'
      }
    },
    data() {
      return {
        counter: 0,
        currentBrand: null,
        selected: 1,
        isLeaving: false,
        isEntering: false,
        isSliderLoop: true,
        slidePerPage: [[320, 3], [768, 5]],
        sliderKey: Date.now(),
        sliderStartAt: this.initialSlide(this.arraySize),
        brandInfo: {
          title: null,
          focus: null,
          text: null,
          label: null,
          baseline: null,
          background: null,
          video: null
        },
        brands: [],
        selectedFilter: null,
        isLoading: false,
        mounted: false,
        noBrandFound: false,
        linkUrl: window.location.origin
      };
    },

    mounted() {
      this.mounted = true;
      this.$nextTick(this.clickableBrand);
    },

    methods: {
      brandsButtons() {
        return this.$el.querySelectorAll('.brand-slider__slide button');
      },

      randomKey() {
        return String(Math.random()).slice(2);
      },

      clickableBrand() {
        this.brandClick = (event) => {
          const isClonedSlide = event.currentTarget.parentNode.classList.contains('slide__cloned');
          let brandIndex = +event.currentTarget.dataset.index;
          const notTheLastInLoop = brandIndex - 1 >= 0;
          const barndSlides = [...this.$el.querySelectorAll('.slider__slide')];
          const clickedSlideIndex = barndSlides.indexOf(event.currentTarget.parentNode.classList);
          const activeSlideIndex = barndSlides.indexOf(this.$el.querySelector('.slider__slide.active'));


          let clickDirection = clickedSlideIndex < activeSlideIndex ? 'backward' : 'forward';

          /*istanbul ignore else */
          if (this.screenSize !== 'small' && this.brands.length === 4) {
            brandIndex += 1;
          }

          if (this.screenSize !== 'small' && isClonedSlide && clickDirection === 'forward') {

            this.$refs.slider.slideTransition = 'none';

            if (notTheLastInLoop) {
              this.$refs.slider.goToPage(brandIndex - 1);
            } else {
              this.$refs.slider.goToPage(0);
            }

            setTimeout(() => {
              this.$refs.slider.slideTransition = `${this.$refs.slider.speed / 1000}s ${this.$refs.slider.easing} transform`;
            }, 10);

          } else if (this.screenSize !== 'small' && isClonedSlide && clickDirection === 'backward') {

            this.$refs.slider.slideTransition = 'none';
            this.$refs.slider.goToPage(brandIndex + 1);

            setTimeout(() => {
              this.$refs.slider.slideTransition = `${this.$refs.slider.speed / 1000}s ${this.$refs.slider.easing} transform`;

              this.$refs.slider.goToPage(brandIndex);
            }, 10);

          } else  {
            this.screenSize === 'small' ? this.$refs.slider.goToPage(brandIndex + 1) : this.$refs.slider.goToPage(brandIndex);
          }
        };

        this.brandsButtons().forEach((brand) => {
          brand.addEventListener('click', e => this.brandClick(e));
        });
      },

      clearBrandClick() {
        if (this.brandClick) {
          this.brandsButtons().forEach((brand) => {
            brand.removeEventListener('click', e => this.brandClick(e));
          });
        }
      },

      initialSlide(number) {
        return Math.floor(Math.random() * number);
      },

      setActiveSlide() {
        const slides = [...this.$el.querySelectorAll('.brand-slider__slide button')];

        slides.forEach(element => element.removeAttribute('aria-selected'));
        this.$el.querySelector('.brand-slider__slide.active button').setAttribute('aria-selected', true);
      },

      getBrandName() {
        this.currentBrand = this.$el.querySelector('.brand-slider__slide.active button').getAttribute('aria-label');
      },

      getCurrentSlide() {
        return document.querySelector('.brand-slider__slider .brand-slider__slide.active');
      },

      handleVideoLazy() {
        setTimeout(()=>{
          const videoElementList = document.querySelectorAll('.video-lazy-bg-load');
          if (videoElementList.length > 0) {
            videoLazyObserver(videoElementList);
          }
        }, 1000);
      },

      updateFilter(id, selected) {
        this.isLoading = true;
        this.clearBrandClick();
        this.selected = selected;
        this.isLeaving = true;
        fetchData(this.filterUrl, { data: { GUID: id } }).then(filteredBrand => {
          this.slidePerPage = filteredBrand.length >= 5 ? [[320, 3], [768, 5]] : [[320, 3], [768, filteredBrand.length >= 5 ? 5 : 3]];
          this.brands = filteredBrand.filter((data)=>{
            return data.issliderdisplay === true;
          });
          if (this.brands.length === 0) {
            this.noBrandFound = true;
          } else {
            this.noBrandFound = false;
          }
          this.sliderStartAt = this.initialSlide(filteredBrand.length);
          this.sliderKey = Date.now();
        })
          .then(() => {
            this.isEntering = true;
            this.clickableBrand();
          });
      },

      updateCarousel() {
        let GUID;
        let size;

        switch (this.screenSize) {
          case 'small':
            size = 0;
            break;

          case 'medium':
            size = 768;
            break;

          case 'tablet':
            size = 768;
            break;

          case 'large':
            size = 1370;
            break;

          default:
            size = 0;
            break;
        }


        this.counter += 1;
        const innerCounter = this.counter;
        this.isLoading = true;

        setTimeout(() => {
          this.isLeaving = true;
          const slide = this.getCurrentSlide();
          GUID = slide.getAttribute('data-GUID');
          /* istanbul ignore else */
          this.counter === innerCounter && fetchData(this.brandsUrl, { data: { GUID: GUID, size } }).then(
            brands => {
              const newSlide = brands;
              this.brandInfo.title = newSlide.title;
              this.brandInfo.focus = newSlide.focus;
              this.brandInfo.text = newSlide.text;
              this.brandInfo.baseline = newSlide.baseline;
              this.brandInfo.background = newSlide.background;
              this.brandInfo.video = newSlide.video;
              this.brandInfo.label = newSlide.label;
              this.brandInfo.link = newSlide.link;
              this.linkUrl = `${window.location.origin}${newSlide.link}`;
              setTimeout(() => {
                this.setActiveSlide();
                this.getBrandName();
                this.isLeaving = false;
                this.isEntering = true;
                this.isLoading = false;
                this.handleVideoLazy();
              }, 200);
            }
          );
        }, 10);
      }
    }
  };
</script>
